import { selectEntitlements, selectUserRoles } from '../selectors';
import {
	ProcessUserContextParams,
	StoreUserContextParams,
	UserContext,
} from '../types';

import { useSelector } from 'react-redux';
import { mapEntitlements } from '../contexts/Forge/utils/mapEntitlements';
import { InnovyzeEntitlement } from '../types/entitlement';
import { InnovyzeRole } from '../utils/InnovyzeRole';

export function useIsAdmin(): boolean {
	const role = useSelector(selectUserRoles);
	return role === InnovyzeRole.ADMIN || role === InnovyzeRole.SUPER_ADMIN;
}

// This is no longer used, replaced by subscriptions in lib_onu_shared
export function useIsEntitled(entitlement: InnovyzeEntitlement): boolean {
	const entitlements: string[] = useSelector(selectEntitlements);
	return entitlements.includes(entitlement);
}

// Call this to get a list of the subscriptions for the current user
export function useGetSubscriptions(): string[] {
	return useSelector(selectEntitlements);
}

export const parseNameParts = (
	fullName: string,
): { firstName?: string; lastName?: string } => {
	const [firstName, lastName] = (fullName || '')
		.split(' ')
		.filter(c => c !== '');
	return { firstName, lastName };
};

const appendEmulatorContext = (
	userContext: Partial<UserContext>,
	{ autodeskUserContext, userProfile }: ProcessUserContextParams,
) => {
	if (!userContext.inEmulation) {
		return userContext;
	}

	userContext.emulatorUser = {
		name: userProfile.firstName + ' ' + userProfile.lastName,
		id: autodeskUserContext?.emulatorOxygenId,
		email: userProfile.emailId,
	};
};

export const processUserContextToStore = (
	processParams: ProcessUserContextParams,
	isLandingPage?: boolean,
	isProjectPickerOn?: boolean,
): StoreUserContextParams => {
	let defaultSite;
	const {
		autodeskUserContext,
		autodeskUserTeams,
		autodeskUserProjects,
		userProfile,
		accessToken,
	} = processParams;

	const entitlementsSet = new Set<string>([
		...mapEntitlements(autodeskUserContext?.currentTeamLicenses ?? []),
		...mapEntitlements(autodeskUserContext?.otherTeamLicenses ?? []),
	]);

	const defaultTeam = autodeskUserContext?.companyId;
	const defaultProjectUrn = autodeskUserContext?.projectId;
	if (isProjectPickerOn) {
		// Try with nothing first
		const activeProject = autodeskUserProjects?.find(
			project => autodeskUserContext?.projectId === project.id,
		);
		defaultSite = {
			tenantId: autodeskUserContext?.organizationId ?? '',
			teamName:
				activeProject?.name ?? autodeskUserContext?.organizationId,
		};
	}
	// TODO Old flow - remove when finished
	else {
		defaultSite = autodeskUserTeams?.teams.find(team => {
			const teamProjectUrn = team.projectId;
			if (defaultProjectUrn && teamProjectUrn) {
				return teamProjectUrn === defaultProjectUrn;
			}

			return team.teamId === defaultTeam;
		});
	}

	const innovyzeRole = !isLandingPage
		? autodeskUserContext?.role
		: InnovyzeRole.ADMIN;
	const userContext: Partial<UserContext> = {
		defaultSite: !isLandingPage
			? defaultSite?.tenantId ?? autodeskUserContext?.organizationId
			: 'landingPage',
		defaultSiteProjectId: autodeskUserContext?.projectId ?? '',
		defaultTeamId: autodeskUserContext?.companyId ?? '',
		innovyzeRole,
		id: userProfile.userId,
		email: userProfile.emailId,
		name: autodeskUserContext?.name,
		entitlements: Array.from(entitlementsSet),
		inEmulation: autodeskUserContext?.inEmulation,
		subscriptions: autodeskUserContext?.subscriptions,
		teamId: autodeskUserContext?.teamId ?? '',
		currentTeamLicenses: autodeskUserContext?.currentTeamLicenses,
		otherTeamLicenses: autodeskUserContext?.otherTeamLicenses,
		productAccessLevels: autodeskUserContext?.productAccessLevels
			? [...autodeskUserContext.productAccessLevels]
			: [],
	};
	if (!isLandingPage) appendEmulatorContext(userContext, processParams);

	return {
		userProfile,
		userContext,
		defaultSite:
			defaultSite?.tenantId ?? autodeskUserContext?.organizationId ?? '',
		defaultSiteName: defaultSite?.teamName ?? '',
		defaultSiteProjectId: autodeskUserContext?.projectId ?? '',
		defaultTeamId: autodeskUserContext?.companyId ?? '',
		entitlements: Array.from(entitlementsSet),
		teams: autodeskUserTeams?.teams ?? [],
		accessToken,
	};
};

// legacy exports (incorrect hook shape)
export const isAdmin = useIsAdmin;
export const isEntitled = useIsEntitled;
export const getSubscriptions = useGetSubscriptions;
